import { say } from "./words.ts";

/**
 * Header carrying the editor capability for dev-server file mutations from a LAN page.
 */
export const EDITOR_CAPABILITY_HEADER = "X-Odyssey-Editor-Capability";

const CAPABILITY_REQUIRED = "required";
const STORAGE_KEY = "odyssey.editor-capability";

/**
 * The page operations used to retain and request a capability, separated so the flow is tested without a DOM.
 */
export interface CapabilityPage {
  readonly origin: string;
  readonly read: () => string | null;
  readonly keep: (token: string) => void;
  readonly forget: () => void;
  readonly ask: () => string | null;
}

/**
 * Fetch shape used by the editor clients and feedback widget.
 */
export type DevServerFetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Wraps fetch for same-origin dev-server mutations. A LAN refusal marked `required` asks for the capability
 * the server keeps in its file, keeps it for this tab in `sessionStorage`, and retries once. Reads, non-API
 * writes and requests to another origin never carry the capability.
 *
 * @param page Origin, storage and prompt operations.
 * @param fetcher Underlying fetch.
 * @returns The guarded fetch.
 */
export function createDevServerFetch(page: CapabilityPage, fetcher: DevServerFetch): DevServerFetch {
  return async (input, init) => {
    if (!isSameOriginApiMutation(input, init, page.origin)) {
      return fetcher(input, init);
    }
    const kept = page.read();
    const first = await fetcher(input, withCapability(init, kept));
    if (first.status !== 403 || first.headers.get(EDITOR_CAPABILITY_HEADER) !== CAPABILITY_REQUIRED) {
      return first;
    }
    if (kept !== null) {
      page.forget();
    }
    const entered = page.ask()?.trim() ?? "";
    if (entered === "") {
      return first;
    }
    page.keep(entered);
    return fetcher(input, withCapability(init, entered));
  };
}

/**
 * Whether a request may write files through this page's dev server.
 *
 * @param input Request URL.
 * @param init Request options.
 * @param origin Page origin.
 * @returns True for a non-read method below the same origin's `/api/`.
 */
function isSameOriginApiMutation(input: string, init: RequestInit | undefined, origin: string): boolean {
  const url = new URL(input, origin);
  const method = (init?.method ?? "GET").toUpperCase();
  return url.origin === origin && url.pathname.startsWith("/api/") && method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
}

/**
 * Adds a capability header without mutating a caller's headers.
 *
 * @param init Request options.
 * @param capability Capability, or null for the first request from a page that has none.
 * @returns Request options with the header when present.
 */
function withCapability(init: RequestInit | undefined, capability: string | null): RequestInit | undefined {
  if (capability === null) {
    return init;
  }
  const headers = new Headers(init?.headers);
  headers.set(EDITOR_CAPABILITY_HEADER, capability);
  return { ...init, headers };
}

/**
 * Reads the capability kept for this tab, treating unavailable storage as empty.
 *
 * @returns The capability, or null.
 */
function readBrowserCapability(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Keeps a capability for this tab when storage is available.
 *
 * @param token Capability entered by the author.
 */
function keepBrowserCapability(token: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    return;
  }
}

/**
 * Forgets a capability the dev server refused, which happens once the server has restarted with another one: its
 * kept file deleted to revoke it, or `EDITOR_CAPABILITY` set to a different value.
 */
function forgetBrowserCapability(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
}

/**
 * Asks the author for the dev server's capability, which they read from its kept file.
 *
 * @returns Entered text, or null when cancelled.
 */
function askBrowserCapability(): string | null {
  return window.prompt(say("request.capabilityPrompt"));
}

/**
 * Browser page implementation. The capability stays in this tab and is never put in a URL.
 */
const BROWSER_PAGE: CapabilityPage = {
  get origin(): string {
    return window.location.origin;
  },
  read: readBrowserCapability,
  keep: keepBrowserCapability,
  forget: forgetBrowserCapability,
  ask: askBrowserCapability,
};

/**
 * Fetch for dev-server API calls made by the editor and the development feedback widget.
 */
export const devServerFetch: DevServerFetch = createDevServerFetch(BROWSER_PAGE, (input, init) => fetch(input, init));
